import { CanActivateFn, UrlTree, Router } from '@angular/router';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { isLoggedIn } from '../store/auth/auth.selectors';
import { Auth, onAuthStateChanged, User as FirebaseUser } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { map, take, switchMap, catchError, of, from } from 'rxjs';

type UserGateState = {
  banned: boolean;
  locationCompleted: boolean;
  basePathCompleted: boolean; // ✅ Camino Base (20 retos)
};

// ✅ Ajusta si cambias tus rutas
const TODAY_HOME = '/tabs/today';

// ✅ Ajusta esta ruta si tu “Camino Base (mix)” vive en otro sitio.
const BASE_PATH_HOME = '/tabs/explore';

// ✅ Ruta “elegir ubicación” (no obligatoria en MVP)
const LOCATION_PICKER_ROUTE = '/choose-location';
// ✅ legacy
const LOCATION_ONBOARDING_ROUTE = '/location-onboarding';

// ✅ Cache en memoria para evitar lecturas repetidas en Firestore
let cachedUid: string | null = null;
let cachedState: UserGateState | null = null;
let cachedAt = 0;
let inFlight: Promise<UserGateState> | null = null;

// TTL corto (puedes subirlo si quieres)
const CACHE_TTL_MS = 60_000;

function shouldUseCache(uid: string) {
  return cachedUid === uid && cachedState !== null && Date.now() - cachedAt < CACHE_TTL_MS;
}

/** ✅ Si el onboarding ya guardó flags locales, los usamos para evitar rebotes por caché */
function readLocalLocationCompleted(): boolean {
  try {
    const v =
      localStorage.getItem('locationCompleted') === '1' ||
      localStorage.getItem('challup_location_completed') === '1' ||
      localStorage.getItem('onboardingLocationDone') === '1' ||
      localStorage.getItem('hasOnboardedLocation') === '1';
    return !!v;
  } catch {
    return false;
  }
}

/** ✅ Flag local opcional para el camino base (si lo usas en algún punto) */
function readLocalBasePathCompleted(): boolean {
  try {
    const v =
      localStorage.getItem('basePathCompleted') === '1' ||
      localStorage.getItem('caminoBaseCompleted') === '1' ||
      localStorage.getItem('caminoBaseCompletado') === '1' ||
      localStorage.getItem('seasonCompleted') === '1';
    return !!v;
  } catch {
    return false;
  }
}

function urlAllowsOnboardingEvenIfCompleted(url: string): boolean {
  // ✅ Permitir modo edición / reset aunque locationCompleted sea true
  return url.includes('mode=edit') || url.includes('edit=1') || url.includes('reset=1');
}

function isLocationPickerUrl(url: string): boolean {
  // ✅ Aceptamos ambas rutas (nueva y legacy) por compatibilidad
  return url.startsWith(LOCATION_PICKER_ROUTE) || url.startsWith(LOCATION_ONBOARDING_ROUTE);
}

/**
 * ✅ Detecta si el usuario ya completó el Camino Base (20 retos).
 * Ajusta los nombres de campo a lo que tengas en Firestore.
 */
function computeBasePathCompleted(data: any): boolean {
  // 1) Flags booleanos explícitos
  if (
    data?.basePathCompleted === true ||
    data?.caminoBaseCompleted === true ||
    data?.caminoBaseCompletado === true ||
    data?.seasonCompleted === true ||
    data?.starterCompleted === true ||
    data?.introCompleted === true
  ) {
    return true;
  }

  // 2) Contadores
  const countCandidates = [
    data?.completedBaseCount,
    data?.baseCompletedCount,
    data?.basePathCompletedCount,
    data?.completedIntroCount,
  ];
  for (const v of countCandidates) {
    if (typeof v === 'number' && v >= 20) return true;
  }

  // 3) Arrays
  const arrCandidates = [
    data?.completedBaseDays,
    data?.completedBase,
    data?.completedChallenges,
    data?.baseCompletedDays,
  ];
  for (const v of arrCandidates) {
    if (Array.isArray(v) && v.length >= 20) return true;
  }

  return false;
}

/**
 * ✅ ANDROID/iOS FIX:
 * A veces auth.currentUser tarda en poblarse tras signInWithCredential().
 * Esperamos a onAuthStateChanged un poco antes de decidir que "no hay user".
 */
function waitForCurrentUser(auth: Auth, timeoutMs = 1500): Promise<FirebaseUser | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      try {
        unsub?.();
      } catch {}
      resolve(auth.currentUser ?? null);
    }, timeoutMs);

    const unsub = onAuthStateChanged(auth, (u) => {
      clearTimeout(timer);
      try {
        unsub?.();
      } catch {}
      resolve(u ?? null);
    });

    // por si ya está
    const now = auth.currentUser;
    if (now) {
      clearTimeout(timer);
      try {
        unsub?.();
      } catch {}
      resolve(now);
    }
  });
}

async function loadUserGateState(firestore: Firestore, uid: string): Promise<UserGateState> {
  // Cache válida
  if (shouldUseCache(uid)) {
    return cachedState as UserGateState;
  }

  // Si ya hay lectura en curso, reutilizamos
  if (inFlight && cachedUid === uid) {
    return inFlight;
  }

  cachedUid = uid;

  inFlight = (async () => {
    const ref = doc(firestore, 'users', uid);
    const snap = await getDoc(ref);
    const data = snap.exists() ? (snap.data() as any) : {};

    const state: UserGateState = {
      banned: !!data?.banned,
      locationCompleted: data?.locationCompleted === true,
      basePathCompleted: computeBasePathCompleted(data),
    };

    // ✅ si local dice “completado”, lo respetamos para evitar rebotes por caché
    if (readLocalLocationCompleted()) state.locationCompleted = true;
    if (readLocalBasePathCompleted()) state.basePathCompleted = true;

    cachedState = state;
    cachedAt = Date.now();
    return state;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

export const AuthGuard: CanActivateFn = (route, state) => {
  const store = inject(Store);
  const auth = inject(Auth);
  const firestore = inject(Firestore);
  const router = inject(Router);

  return store.select(isLoggedIn).pipe(
    take(1),
    switchMap((loggedIn) => {
      // ✅ Si ya estamos yendo a login, dejar pasar (evita bucles)
      if (state.url.startsWith('/login')) {
        return of(true);
      }

      if (!loggedIn) {
        return of(router.parseUrl('/login'));
      }

      // ✅ Permitir entrar a banned (si el usuario está baneado lo mandaremos aquí)
      if (state.url.startsWith('/banned')) {
        return of(true);
      }

      // ✅ Obtener user (con espera corta en Android/iOS si todavía no está listo)
      return from(
        (async () => {
          const direct = auth.currentUser;
          if (direct) return direct;

          // Espera breve a que AngularFire se entere del usuario
          return await waitForCurrentUser(auth, 1500);
        })()
      ).pipe(
        switchMap((user) => {
          if (!user) {
            return of(router.parseUrl('/login'));
          }

          // ✅ Si tenemos cache, decisión inmediata
          if (shouldUseCache(user.uid)) {
            const gate = { ...(cachedState as UserGateState) };

            // ✅ Si localStorage marca “completado”, actualizamos cache para no rebotar
            if (!gate.locationCompleted && readLocalLocationCompleted()) {
              gate.locationCompleted = true;
              cachedState = gate;
              cachedAt = Date.now();
            }
            if (!gate.basePathCompleted && readLocalBasePathCompleted()) {
              gate.basePathCompleted = true;
              cachedState = gate;
              cachedAt = Date.now();
            }

            if (gate.banned) {
              return of(router.parseUrl('/banned'));
            }

            // ✅ MVP: ubicación NO obligatoria. Pantalla siempre accesible
            if (isLocationPickerUrl(state.url)) {
              if (urlAllowsOnboardingEvenIfCompleted(state.url)) return of(true);
              return of(true);
            }

            //  ✅ Camino Base completado -> Today deja de ser home
            if (gate.basePathCompleted && state.url.startsWith('/tabs/today')) {
              return of(router.parseUrl(BASE_PATH_HOME));
            }

            return of(true);
          }

          // ✅ Si no hay cache, leemos 1 vez Firestore (y cacheamos)
          return from(loadUserGateState(firestore, user.uid)).pipe(
            map((gate): boolean | UrlTree => {
              // 1) Banned
              if (gate.banned) {
                return router.parseUrl('/banned');
              }

              // 2) MVP: ubicación NO obligatoria, pantalla siempre accesible
              if (isLocationPickerUrl(state.url)) {
                if (urlAllowsOnboardingEvenIfCompleted(state.url)) return true;
                return true;
              }

              // 3) Camino Base completado -> Today deja de ser home
              if (gate.basePathCompleted && state.url.startsWith('/tabs/today')) {
                return router.parseUrl(BASE_PATH_HOME);
              }

              return true;
            }),
            catchError((err) => {
              console.warn('⚠️ Error en AuthGuard al leer usuario:', err);
              // Si falla lectura: dejamos pasar para no bloquear
              return of(true);
            })
          );
        })
      );
    })
  );
};
